import {
  OktonInput,
  OktonAlert,
  OktonSearchSelect,
  OktonPageHeader,
} from '@okton/platform-ui';
import {
  Box,
  Button,
  Flex,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { Check } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import BankAppPage from '../../../layout/BankAppPage';
import {
  rotuloMunicipio,
  useAgenciasCadastro,
  useMunicipios,
  useSaveAgencia,
} from '../../../services/hooks/useAgencias';
import {
  BANCO_COM_POSTO,
  useBancos,
} from '../../../services/hooks/useContasBancarias';

const VAZIO = {
  banco: '',
  agencia: '',
  digito: '',
  posto: '',
  descricao: '',
  gerente: '',
  telefone: '',
  telefone2: '',
  cnpj: '',
  cep: '',
  rua: '',
  numero: '',
  complemento: '',
  bairro: '',
  municipio: '',
};

/**
 * Cadastro de uma Agência Bancária.
 *
 * O IFLEX troca Dígito por Posto quando o Banco é o Sicredi (código de
 * compensação 748), então a tela pede o mesmo campo que ele pede. O que não
 * for informado é gravado como zero: é preenchimento que o IFLEX espera na
 * coluna, não faz parte da numeração.
 *
 * A coluna da esquerda é o que identifica a Agência e sem o que não dá para
 * gravar; a da direita é contato e endereço, todo opcional. Separadas assim,
 * quem só quer cadastrar a numeração não precisa percorrer o resto.
 */
const AgenciaForm = () => {
  const history = useHistory();
  const bg = useColorModeValue('white', 'gray.800');
  const color = useColorModeValue('#3f3f46', '#d4d4d8');
  const { idAgencia } = useParams<{ idAgencia?: string }>();
  const edicao = !!idAgencia;

  const { data: agencias = [] } = useAgenciasCadastro();
  const { data: bancos = [] } = useBancos();
  const saveMutation = useSaveAgencia();

  const [form, setForm] = useState({ ...VAZIO });
  const [erro, setErro] = useState('');
  // O que está escrito no campo de Município, que não é o código gravado: o
  // código só existe depois que o texto casar com um Município da lista.
  const [municipioTexto, setMunicipioTexto] = useState('');

  const registro = agencias.find(a => a.id_agc === idAgencia);
  const { data: municipios = [] } = useMunicipios(
    municipioTexto,
    form.municipio,
  );

  // A lista chega depois do primeiro render: sem isso a edição abriria vazia.
  useEffect(() => {
    if (!registro) return;
    setForm({
      banco: registro.banco_agc ?? '',
      agencia: registro.agencia_agc ?? '',
      // O zero é preenchimento: mostrá-lo faria o usuário achar que a agência
      // tem dígito 0.
      digito:
        registro.digito_agc && registro.digito_agc !== '0'
          ? registro.digito_agc
          : '',
      posto:
        registro.posto_agc && registro.posto_agc !== '0'
          ? registro.posto_agc
          : '',
      descricao: registro.descricao_agc ?? '',
      gerente: registro.gerente_agc ?? '',
      telefone: registro.telefone_agc ?? '',
      telefone2: registro.telefone2_agc ?? '',
      cnpj: registro.cnpj_agc ?? '',
      cep: registro.cep_agc ?? '',
      rua: registro.rua_agc ?? '',
      numero: registro.numero_agc ?? '',
      complemento: registro.complemento_agc ?? '',
      bairro: registro.bairro_agc ?? '',
      municipio: registro.municipio_agc ?? '',
    });
    setMunicipioTexto(rotuloMunicipio(registro));
  }, [registro]);

  const bancoUsaPosto =
    bancos.find(b => b.id_ban === form.banco)?.confcomp_ban === BANCO_COM_POSTO;

  // O datalist devolve o rótulo escolhido, não um código: é por ele que se
  // acha o Município de volta. Texto que não casa com nenhum deixa o código
  // vazio, e a Agência é gravada sem Município — o campo é opcional.
  const escolherMunicipio = (texto: string) => {
    setMunicipioTexto(texto);
    const achado = municipios.find(m => rotuloMunicipio(m) === texto);
    setForm(f => ({ ...f, municipio: achado ? achado.id_mun : '' }));
  };

  const campo = (
    rotulo: string,
    chave: keyof typeof VAZIO,
    limite: number,
    dica = '',
  ) => (
    <Box>
      <Text
        as="label"
        htmlFor={`agencia-${chave}`}
        display="block"
        fontSize="sm"
        fontWeight="medium"
        mb="1"
      >
        {rotulo}
      </Text>
      <OktonInput
        name={`agencia-${chave}`}
        size="lg"
        rounded="2xl"
        fontWeight="light"
        color={color}
        maxLength={limite}
        placeholder={dica}
        value={form[chave]}
        onChange={e => {
          // O updater pode executar após o evento; capture o valor digitado.
          const valor = e.target.value;
          setForm(f => ({ ...f, [chave]: valor }));
        }}
      />
    </Box>
  );

  const salvar = async () => {
    if (!form.banco) {
      setErro('Informe o Banco da Agência.');
      return;
    }
    if (!form.agencia.trim()) {
      setErro('Informe o número da Agência.');
      return;
    }
    setErro('');
    try {
      await saveMutation.mutateAsync({
        id: idAgencia,
        banco: form.banco,
        agencia: form.agencia.trim(),
        digito: bancoUsaPosto ? '' : form.digito.trim(),
        posto: bancoUsaPosto ? form.posto.trim() : '',
        descricao: form.descricao.trim(),
        gerente: form.gerente.trim(),
        telefone: form.telefone.trim(),
        telefone2: form.telefone2.trim(),
        cnpj: form.cnpj.trim(),
        cep: form.cep.trim(),
        rua: form.rua.trim(),
        numero: form.numero.trim(),
        complemento: form.complemento.trim(),
        bairro: form.bairro.trim(),
        municipio: form.municipio,
      });
      history.push('/agencias');
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Erro ao gravar a Agência.');
    }
  };

  return (
    <BankAppPage>
      <OktonPageHeader
        eyebrow="CADASTROS"
        title={edicao ? 'Editar Agência' : 'Nova Agência'}
        subtitle="Agência bancária usada pelas Contas"
        onBack={() => history.push('/agencias')}
      />

      <Box bg={bg} mt="4" borderWidth="1px" rounded="2xl" p="6">
        {/* Sem largura máxima: as duas colunas dividem o cartão inteiro, como
        na tela de Novo Lançamento. Limitar a 1100px deixava uma faixa branca à
        direita em telas largas. */}
        <Stack spacing="4">
          {!!erro && <OktonAlert status="error" description={erro} />}

          <SimpleGrid columns={[1, 1, 2]} spacing="8">
            <Stack spacing="4">
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb="1">
                  Banco *
                </Text>
                <OktonSearchSelect
                  name="banco"
                  placeholder="Selecione o Banco"
                  value={form.banco}
                  onChange={valor => setForm(f => ({ ...f, banco: valor }))}
                  options={[
                    ...bancos.map(b => ({
                      value: b.id_ban,
                      label: b.nome_ban,
                    })),
                  ]}
                />
              </Box>

              <SimpleGrid columns={[1, 2]} spacing="4">
                {campo('Número *', 'agencia', 10, 'Ex.: 1234')}
                <Box>
                  <Text
                    as="label"
                    htmlFor={bancoUsaPosto ? 'agencia-posto' : 'agencia-digito'}
                    display="block"
                    fontSize="sm"
                    fontWeight="medium"
                    mb="1"
                  >
                    {bancoUsaPosto ? 'Posto' : 'Dígito'}
                  </Text>
                  {bancoUsaPosto ? (
                    <OktonInput
                      name="agencia-posto"
                      size="lg"
                      rounded="2xl"
                      fontWeight="light"
                      color={color}
                      maxLength={2}
                      value={form.posto}
                      onChange={e => {
                        const valor = e.target.value;
                        setForm(f => ({ ...f, posto: valor }));
                      }}
                    />
                  ) : (
                    <OktonInput
                      name="agencia-digito"
                      size="lg"
                      rounded="2xl"
                      fontWeight="light"
                      color={color}
                      maxLength={1}
                      value={form.digito}
                      onChange={e => {
                        const valor = e.target.value;
                        setForm(f => ({ ...f, digito: valor }));
                      }}
                    />
                  )}
                  <Text fontSize="xs" color="gray.500" mt="1">
                    O Sicredi identifica a agência pelo Posto; os demais bancos,
                    pelo Dígito.
                  </Text>
                </Box>
              </SimpleGrid>

              {campo(
                'Descrição',
                'descricao',
                60,
                'Em branco, usa o número com o dígito',
              )}
              {campo('CNPJ', 'cnpj', 18, 'Só números')}

              <Text fontSize="xs" color="gray.500">
                A Agência é cadastro do IFLEX: duas Agências do mesmo Banco não
                podem ter a mesma numeração, e a que já estiver em uso por
                alguma Conta não pode ser excluída.
              </Text>
            </Stack>

            <Stack spacing="4">
              <Text fontSize="xs" color="gray.500" textTransform="uppercase">
                Contato e endereço (opcional)
              </Text>

              {campo('Gerente', 'gerente', 60)}

              <SimpleGrid columns={[1, 2]} spacing="4">
                {campo('Telefone', 'telefone', 20)}
                {campo('Telefone 2', 'telefone2', 20)}
              </SimpleGrid>

              <SimpleGrid columns={[1, 3]} spacing="4">
                <Box>{campo('CEP', 'cep', 9, 'Só números')}</Box>
                <Box gridColumn={[null, 'span 2']}>
                  {campo('Rua', 'rua', 60)}
                </Box>
              </SimpleGrid>

              <SimpleGrid columns={[1, 2]} spacing="4">
                {campo('Número', 'numero', 10)}
                {campo('Complemento', 'complemento', 20)}
              </SimpleGrid>

              {campo('Bairro', 'bairro', 60)}

              <Box>
                <Text
                  as="label"
                  htmlFor="agencia-municipio"
                  display="block"
                  fontSize="sm"
                  fontWeight="medium"
                  mb="1"
                >
                  Município
                </Text>
                <OktonInput
                  name="agencia-municipio"
                  size="lg"
                  rounded="2xl"
                  fontWeight="light"
                  color={color}
                  list="municipios-agencia"
                  placeholder="Digite o nome do município"
                  value={municipioTexto}
                  onChange={e => escolherMunicipio(e.target.value)}
                />
                <datalist id="municipios-agencia">
                  {/* Sem atributo value: o texto da option já é o valor que o
                  navegador completa, e é por ele que o município é achado de
                  volta. */}
                  {municipios.map(m => (
                    <option key={m.id_mun}>{rotuloMunicipio(m)}</option>
                  ))}
                </datalist>
                <Text fontSize="xs" color="gray.500" mt="1">
                  {municipioTexto.trim() === ''
                    ? 'Digite ao menos duas letras para ver os municípios.'
                    : form.municipio
                    ? 'Município selecionado.'
                    : 'Escolha um município da lista para que ele seja gravado.'}
                </Text>
              </Box>
            </Stack>
          </SimpleGrid>

          <Flex gridGap="3" pt="2">
            <Button variant="ghost" onClick={() => history.push('/agencias')}>
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              leftIcon={
                saveMutation.isLoading ? (
                  <Spinner size="sm" />
                ) : (
                  <Check size={16} />
                )
              }
              isDisabled={saveMutation.isLoading}
              onClick={salvar}
            >
              Salvar
            </Button>
          </Flex>
        </Stack>
      </Box>
    </BankAppPage>
  );
};

export default AgenciaForm;
